import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const cardActionVariants = cva(
  'inline-flex items-center font-medium transition-colors disabled:opacity-50',
  {
    variants: {
      color: {
        default: 'text-slate-600 hover:text-slate-900 hover:bg-slate-100',
        blue: 'text-slate-600 hover:text-blue-700 hover:bg-blue-50',
        emerald: 'text-slate-600 hover:text-emerald-700 hover:bg-emerald-50',
        indigo: 'text-slate-600 hover:text-indigo-700 hover:bg-indigo-50',
        amber: 'text-slate-600 hover:text-amber-700 hover:bg-amber-50',
        red: 'text-red-500 hover:text-red-700 hover:bg-red-50',
      },
      iconOnly: {
        true: 'p-2 rounded-lg',
        false: 'gap-1.5 px-3 py-1.5 text-sm rounded-lg',
      },
    },
    compoundVariants: [
      // Icon-only buttons use lighter base color
      { color: 'default', iconOnly: true, className: 'text-slate-400 hover:text-slate-600' },
      { color: 'blue', iconOnly: true, className: 'text-slate-400 hover:text-blue-600' },
      { color: 'emerald', iconOnly: true, className: 'text-slate-400 hover:text-emerald-600' },
      { color: 'indigo', iconOnly: true, className: 'text-slate-400 hover:text-indigo-600' },
      { color: 'amber', iconOnly: true, className: 'text-slate-400 hover:text-amber-600' },
      { color: 'red', iconOnly: true, className: 'text-red-400 hover:text-red-600' },
    ],
    defaultVariants: {
      color: 'default',
      iconOnly: false,
    },
  }
);

export interface CardActionProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>,
    VariantProps<typeof cardActionVariants> {}

const CardAction = React.forwardRef<HTMLButtonElement, CardActionProps>(
  ({ className, color, iconOnly, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(cardActionVariants({ color, iconOnly, className }))}
        {...props}
      />
    );
  }
);
CardAction.displayName = 'CardAction';

export { CardAction, cardActionVariants };
